import {PathUtility} from 'src/shared/constants/PathBuilder';
import {Badge, Menu, NavLink} from '@mantine/core';
import {Avatar} from 'src/design/atoms/Avatar';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {
  IconChevronRight,
  IconHelp,
  IconLogout,
  IconPhone,
  IconShieldLock,
} from '@tabler/icons-react';
import React from 'react';
import {Link} from 'react-router-dom';
import {useAppSelector} from 'src/redux/hooks';
import {canParseJson} from 'src/utilities/objectUtils';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';
import {tryParse} from 'src/utilities/objectUtils';
import classes from './AvatarMenu.module.css';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface AvatarMenuProps {
  compact?: boolean;
}
export const AvatarMenu: React.FC<AvatarMenuProps> = ({compact = false}) => {
  const {avatarUrl: rawAvatarUrl, user} = useAppSelector(i => i.session);
  const [currentLanguage] = React.useState<string>('English');
  const [currentDateFormat] = React.useState<string>('MM/dd/yyyy');

  const {accessToken} = useAppSelector(state => state.auth);

  const navigate = useAppNavigate();

  const handleLogOut = () => {
    navigate(PathUtility.Logout());
  };

  const parsed = tryParse(rawAvatarUrl);
  const isArray =
    rawAvatarUrl && canParseJson(rawAvatarUrl) && Array.isArray(parsed);
  const avatarUrl =
    rawAvatarUrl && (isArray ? parsed[0] && parsed[0].value : parsed.value);

  const getInitials = (): string => {
    if (!user?.name) {
      return '';
    }

    const split = user?.name.split(' ');
    if (split.length < 2) {
      return split[0][0].toUpperCase();
    }

    return `${split[0][0]}${split[1][0]}`.toUpperCase();
  };

  return (
    <Menu position="right-end" width={260}>
      <Menu.Target>
        <UnstyledButton className={classes.user} aria-label="avatarMenuUI">
          {compact ? (
            <Avatar
              src={
                avatarUrl
                  ? getUrlWithEncodingAndAuth(avatarUrl, accessToken)
                  : undefined
              }
              radius="xl">
              {!avatarUrl && getInitials()}
            </Avatar>
          ) : (
            <Group>
              <Avatar
                src={
                  avatarUrl
                    ? getUrlWithEncodingAndAuth(avatarUrl, accessToken)
                    : undefined
                }
                radius="xl">
                {!avatarUrl && getInitials()}
              </Avatar>

              <Box style={{flex: 1}}>
                <Text c="white" size="sm" fw={500}>
                  {user?.name}
                </Text>

                <Text c="dimmed" size="xs">
                  {user?.jobTitle}
                </Text>
              </Box>

              <IconChevronRight size={14} stroke={1.5} color="white" />
            </Group>
          )}
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Stack gap="xs" p="sm" visibleFrom="sm">
          <Text fw={500}>{user?.name}</Text>
          {user?.jobTitle && <Text size="sm">{user?.jobTitle}</Text>}
          {user?.supervisorName && (
            <Text size="sm">Direct Manager: {user?.supervisorName}</Text>
          )}
          {user?.departmentName && (
            <Text size="sm">{user?.departmentName}</Text>
          )}
        </Stack>

        <NavLink
          disabled
          label={
            <Group>
              <span>App Activation</span>
              <Badge size="sm" variant="light">
                Coming Soon
              </Badge>
            </Group>
          }
        />

        <NavLink disabled label={`Language - ${currentLanguage}`} />

        <NavLink disabled label={`Date Format - ${currentDateFormat}`} />

        <Divider my="xs" />

        <Menu.Item
          aria-label="supportBtnUI"
          leftSection={<IconHelp size={16} />}
          onClick={() =>
            window.open('https://proactehs.com/support/', '_blank')
          }>
          Help
        </Menu.Item>

        <Menu.Item
          aria-label="contactBtnUI"
          leftSection={<IconPhone size={16} />}
          onClick={() =>
            window.open('https://proactehs.com/contact/', '_blank')
          }>
          Talk to Sales
        </Menu.Item>

        <Divider my="xs" />

        <Menu.Item
          component={Link}
          to={PathUtility.Security()}
          leftSection={<IconShieldLock size={16} />}>
          Multi-Factor Authentication
        </Menu.Item>

        <Menu.Item
          aria-label="logoutBtnUI"
          leftSection={<IconLogout size={16} />}
          onClick={handleLogOut}>
          Logout
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
